import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import Box from '@mui/material/Box';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import frLocale from 'i18n-iso-countries/langs/fr.json';

import { FormDrawer, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { ISociety } from '@bloomays-lib/types.shared';
import {
  getSocietyStatusInfos,
  isCountryAllowedForSocietyStatus,
  FR_SIRET_REGEX_STRING,
} from '@bloomays-lib/utils.shared';
import {
  UPSERT_SOCIETY,
  UPDATE_MISSION,
  LINK_SOCIETY_TO_MISSION,
  UNLINK_SOCIETY_TO_MISSION,
  SEARCH_SOCIETY,
} from '@bloomays-lib/adapter.api-bloomer';

import { cancel, notify } from '../../../../helpers/toastify';
import {
  FieldV2,
  InputV2,
  SelectV2,
  Row2,
  InfoV2,
  DividerV2,
  CheckRow,
  SectionHeading,
  type SelectV2Option,
} from '../formFields';

countries.registerLocale(frLocale);

const frSiretRegex = new RegExp(FR_SIRET_REGEX_STRING);

/** Drapeau emoji depuis un code ISO-2 (les <option> natives ne peuvent pas afficher d'image). */
const codeToFlag = (iso2: string): string =>
  iso2.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

export type EditClientSocietyDrawerProps = {
  open: boolean;
  onClose: () => void;
  clientSociety: ISociety;
  clientBillingSociety?: ISociety | null;
  missionRecordId: string;
  contractType?: string | null;
  /** 'client' (société cliente, défaut) ou 'billing' (société de facturation rattachée). */
  variant?: 'client' | 'billing';
};

/**
 * Gestion de la société cliente en panneau latéral (DrawerV2) — édition via `UPSERT_SOCIETY`
 * (identité, adresse, délai de paiement, règles de facturation) + type de contrat de la mission
 * via `UPDATE_MISSION`. La société de facturation rattachée est affichée pour contexte.
 */
export const EditClientSocietyDrawer = ({
  open,
  onClose,
  clientSociety,
  clientBillingSociety,
  missionRecordId,
  contractType: initialContractType,
  variant = 'client',
}: EditClientSocietyDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);
  const isBilling = variant === 'billing';

  const SOCIETY_STATUS_OPTIONS = [
    { value: 'EI', label: t('drawers.editClientSociety.statusOptions.EI', { ns: 'missionDashboard' }) },
    { value: 'EURL', label: t('drawers.editClientSociety.statusOptions.EURL', { ns: 'missionDashboard' }) },
    { value: 'SARL', label: t('drawers.editClientSociety.statusOptions.SARL', { ns: 'missionDashboard' }) },
    { value: 'SAS', label: t('drawers.editClientSociety.statusOptions.SAS', { ns: 'missionDashboard' }) },
    { value: 'SASU', label: t('drawers.editClientSociety.statusOptions.SASU', { ns: 'missionDashboard' }) },
    { value: 'Autre', label: t('drawers.editClientSociety.statusOptions.Autre', { ns: 'missionDashboard' }) },
    { value: 'SUE', label: t('drawers.editClientSociety.statusOptions.SUE', { ns: 'missionDashboard' }) },
    { value: 'SHUE', label: t('drawers.editClientSociety.statusOptions.SHUE', { ns: 'missionDashboard' }) },
  ];

  const CONTRACT_TYPE_OPTIONS = [
    {
      value: 'bloomaysContract',
      label: t('drawers.editClientSociety.contractTypeOptions.bloomaysContract', { ns: 'missionDashboard' }),
    },
    { value: 'devis', label: t('drawers.editClientSociety.contractTypeOptions.devis', { ns: 'missionDashboard' }) },
    { value: 'BDC', label: t('drawers.editClientSociety.contractTypeOptions.BDC', { ns: 'missionDashboard' }) },
    {
      value: 'clientContract',
      label: t('drawers.editClientSociety.contractTypeOptions.clientContract', { ns: 'missionDashboard' }),
    },
  ];

  const [name, setName] = useState(clientSociety.name ?? '');
  const [status, setStatus] = useState(clientSociety.status ?? '');
  const [siret, setSiret] = useState(clientSociety.siret ?? '');
  const [tva, setTva] = useState(clientSociety.tva ?? '');
  const [addressCountryCode, setAddressCountryCode] = useState(clientSociety.addressCountryCode ?? '');
  const [addressPart1, setAddressPart1] = useState(clientSociety.addressPart1 ?? '');
  const [addressZip, setAddressZip] = useState(clientSociety.addressZip ?? '');
  const [addressTown, setAddressTown] = useState(clientSociety.addressTown ?? '');
  const [paydateDays, setPaydateDays] = useState<string>(
    clientSociety.paydateDays != null ? String(clientSociety.paydateDays) : '',
  );
  const siretFormatError =
    addressCountryCode === 'FR' && siret && !frSiretRegex.test(siret)
      ? t('drawers.editClientSociety.siretFormatError', { ns: 'missionDashboard' })
      : undefined;

  const countryOptions: SelectV2Option[] = useMemo(() => {
    const statusInfos = getSocietyStatusInfos(status);
    const names = countries.getNames('fr', { select: 'official' });
    return Object.keys(names)
      .filter((code) => isCountryAllowedForSocietyStatus(statusInfos, code))
      .map((code) => ({ value: code, label: `${codeToFlag(code)} ${names[code]}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [status]);

  const [autoValidateInvoice, setAutoValidateInvoice] = useState(!!clientSociety.autoValidateInvoice);
  const [orderNumberRequired, setOrderNumberRequired] = useState(!!clientSociety.orderNumberRequired);
  const [contractType, setContractType] = useState(initialContractType ?? '');

  const [upsertSociety, { loading: savingSociety }] = useMutation(UPSERT_SOCIETY);
  const [updateMission, { loading: savingMission }] = useMutation(UPDATE_MISSION);
  const [linkSociety, { loading: linking }] = useMutation(LINK_SOCIETY_TO_MISSION);
  const [unlinkSociety, { loading: unlinking }] = useMutation(UNLINK_SOCIETY_TO_MISSION);

  const distinctBilling = clientBillingSociety && clientBillingSociety.recordId !== clientSociety.recordId;

  // Recherche / création d'une société de facturation (item 6).
  const [billingMode, setBillingMode] = useState<'search' | 'create'>('search');
  const [billingQuery, setBillingQuery] = useState('');
  const [billingName, setBillingName] = useState('');
  const [billingStatus, setBillingStatus] = useState('');
  const [billingSiret, setBillingSiret] = useState('');
  const [billingTva, setBillingTva] = useState('');

  const [searchSocieties, { data: societiesData, loading: loadingSocieties }] = useLazyQuery(SEARCH_SOCIETY);
  // Recherche société cliente à la frappe (nom ou SIRET, serveur) — lodash debounce 500 ms, min 3 caractères.
  const debouncedSearchSocieties = useMemo(
    () => debounce((q: string) => void searchSocieties({ variables: { query: q, isClient: true } }), 500),
    [searchSocieties],
  );
  useEffect(() => () => debouncedSearchSocieties.cancel(), [debouncedSearchSocieties]);
  useEffect(() => {
    if (isBilling || distinctBilling || billingMode !== 'search') {
      debouncedSearchSocieties.cancel();
      return;
    }
    const q = billingQuery.trim();
    if (q.length < 3) {
      debouncedSearchSocieties.cancel();
      return;
    }
    debouncedSearchSocieties(q);
  }, [billingQuery, billingMode, isBilling, distinctBilling, debouncedSearchSocieties]);

  const billingTooShort = billingQuery.trim().length < 3;
  const billingResults = ((societiesData?.searchSociety ?? []) as { recordId: string; name?: string; siret?: string }[])
    .filter((soc) => soc && soc.recordId && soc.recordId !== clientSociety.recordId)
    .slice(0, 8);

  const attachExistingBilling = async (societyId: string) => {
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await linkSociety({ variables: { societyId, missionId: missionRecordId, role: 'clientBilling' } });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const detachBilling = async () => {
    if (!clientBillingSociety?.recordId) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await unlinkSociety({
        variables: { societyId: clientBillingSociety.recordId, missionId: missionRecordId, role: 'clientBilling' },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const createBilling = async () => {
    if (!billingName.trim()) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      const res = await upsertSociety({
        variables: {
          input: {
            isClient: true,
            name: billingName,
            status: billingStatus || undefined,
            siret: billingSiret || undefined,
            tva: billingTva || undefined,
          },
        },
      });
      const newId = res.data?.upsertSociety?.recordId as string | undefined;
      if (!newId) throw new Error(t('drawers.editClientSociety.createSocietyError', { ns: 'missionDashboard' }));
      await linkSociety({ variables: { societyId: newId, missionId: missionRecordId, role: 'clientBilling' } });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const save = async () => {
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertSociety({
        variables: {
          input: {
            recordId: clientSociety.recordId,
            isClient: true,
            name,
            status: status || undefined,
            siret,
            tva: tva || undefined,
            addressCountryCode: addressCountryCode || undefined,
            addressPart1: addressPart1 || undefined,
            addressZip: addressZip || undefined,
            addressTown: addressTown || undefined,
            paydateDays: paydateDays ? Number(paydateDays) : undefined,
            autoValidateInvoice,
            orderNumberRequired,
          },
        },
      });
      if (!isBilling && contractType && contractType !== (initialContractType ?? '')) {
        await updateMission({ variables: { input: { recordId: missionRecordId, contractType } } });
      }
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={
        isBilling
          ? t('drawers.editClientSociety.titleBilling', { ns: 'missionDashboard' })
          : t('drawers.editClientSociety.titleClient', { ns: 'missionDashboard' })
      }
      subtitle={clientSociety.name}
      width={480}
      onSubmit={save}
      submitDisabled={!name.trim() || !!siretFormatError}
      loading={savingSociety || savingMission}
      submitIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
      submitAriaLabel="edit-client-society-submit"
    >
      <FieldV2 label={t('drawers.common.name', { ns: 'missionDashboard' })} required>
        <InputV2
          value={name}
          onChange={setName}
          placeholder={t('drawers.common.businessName', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <Row2>
        <FieldV2 label={t('drawers.common.legalStatus', { ns: 'missionDashboard' })}>
          <SelectV2
            value={status}
            onChange={setStatus}
            options={SOCIETY_STATUS_OPTIONS}
            placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
          />
        </FieldV2>
        <FieldV2 label={t('drawers.common.siret', { ns: 'missionDashboard' })} error={siretFormatError}>
          <InputV2 value={siret} onChange={setSiret} tone={siretFormatError ? 'expired' : undefined} />
        </FieldV2>
      </Row2>
      <FieldV2 label={t('drawers.common.tva', { ns: 'missionDashboard' })}>
        <InputV2 value={tva} onChange={setTva} placeholder={t('drawers.common.optional', { ns: 'missionDashboard' })} />
      </FieldV2>
      <FieldV2 label={t('drawers.editClientSociety.country', { ns: 'missionDashboard' })}>
        <SelectV2
          value={addressCountryCode}
          onChange={setAddressCountryCode}
          options={countryOptions}
          placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
        />
      </FieldV2>

      {!isBilling ? (
        <FieldV2
          label={t('drawers.editClientSociety.contractType', { ns: 'missionDashboard' })}
          hint={t('drawers.editClientSociety.contractTypeHint', { ns: 'missionDashboard' })}
        >
          <SelectV2
            value={contractType}
            onChange={setContractType}
            options={CONTRACT_TYPE_OPTIONS}
            placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
          />
        </FieldV2>
      ) : null}

      <FieldV2 label={t('drawers.editClientSociety.address', { ns: 'missionDashboard' })}>
        <InputV2
          value={addressPart1}
          onChange={setAddressPart1}
          placeholder={t('drawers.editClientSociety.addressPlaceholder', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <Row2>
        <FieldV2 label={t('drawers.editClientSociety.zip', { ns: 'missionDashboard' })}>
          <InputV2 value={addressZip} onChange={setAddressZip} />
        </FieldV2>
        <FieldV2 label={t('drawers.editClientSociety.town', { ns: 'missionDashboard' })}>
          <InputV2 value={addressTown} onChange={setAddressTown} />
        </FieldV2>
      </Row2>
      <FieldV2 label={t('drawers.editClientSociety.paymentDelay', { ns: 'missionDashboard' })}>
        <InputV2 type="number" value={paydateDays} onChange={setPaydateDays} />
      </FieldV2>

      <CheckRow checked={autoValidateInvoice} onChange={setAutoValidateInvoice}>
        {t('drawers.editClientSociety.autoValidateInvoice', { ns: 'missionDashboard' })}
      </CheckRow>
      <CheckRow checked={orderNumberRequired} onChange={setOrderNumberRequired}>
        {t('drawers.editClientSociety.orderNumberRequired', { ns: 'missionDashboard' })}
      </CheckRow>

      {!isBilling ? (
        <>
          <DividerV2 />
          <SectionHeading>{t('drawers.editClientSociety.billingSection', { ns: 'missionDashboard' })}</SectionHeading>
          {distinctBilling ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '11px 13px',
                border: `1px solid ${dsV2.color.border}`,
                borderRadius: dsV2.radius.sm,
                background: dsV2.color.surface2,
              }}
            >
              <ReceiptLongRoundedIcon sx={{ fontSize: 18, color: dsV2.color.t3 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>
                  {clientBillingSociety?.name}
                  {clientBillingSociety?.status ? ` · ${clientBillingSociety.status}` : ''}
                </Box>
                <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>
                  {t('drawers.editClientSociety.distinctEntity', { ns: 'missionDashboard' })}
                </Box>
              </Box>
              <ButtonV2
                variant="danger"
                size="small"
                onClick={detachBilling}
                loading={unlinking}
                startIcon={<LinkOffRoundedIcon sx={{ fontSize: 14 }} />}
              >
                {t('drawers.editClientSociety.detach', { ns: 'missionDashboard' })}
              </ButtonV2>
            </Box>
          ) : (
            <>
              <InfoV2 tone="blue">
                {t('drawers.editClientSociety.noDistinctBilling', { ns: 'missionDashboard' })}
              </InfoV2>
              {/* Bascule Rechercher / Créer (toggle ButtonV2 actif) */}
              <Box sx={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {(['search', 'create'] as const).map((mode) => (
                  <ButtonV2
                    key={mode}
                    variant="secondary"
                    size="small"
                    active={billingMode === mode}
                    onClick={() => setBillingMode(mode)}
                    sx={{ flex: 1 }}
                  >
                    {mode === 'search'
                      ? t('drawers.common.search', { ns: 'missionDashboard' })
                      : t('drawers.common.create', { ns: 'missionDashboard' })}
                  </ButtonV2>
                ))}
              </Box>

              {billingMode === 'search' ? (
                <>
                  <FieldV2
                    label={t('drawers.editClientSociety.searchClientSociety', { ns: 'missionDashboard' })}
                    hint={t('drawers.editClientSociety.searchClientHint', { ns: 'missionDashboard' })}
                  >
                    <InputV2
                      value={billingQuery}
                      onChange={setBillingQuery}
                      placeholder={t('drawers.editClientSociety.searchClientPlaceholder', { ns: 'missionDashboard' })}
                    />
                  </FieldV2>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: 240, overflowY: 'auto' }}>
                    {billingTooShort ? (
                      <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
                        {t('drawers.editClientSociety.searchTooShort', { ns: 'missionDashboard' })}
                      </Box>
                    ) : loadingSocieties ? (
                      <Box sx={{ fontSize: 12, color: dsV2.color.t4, padding: '8px 0' }}>
                        {t('drawers.common.searching', { ns: 'missionDashboard' })}
                      </Box>
                    ) : billingResults.length === 0 ? (
                      <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
                        {t('drawers.editClientSociety.noSocietyFound', { ns: 'missionDashboard' })}
                      </Box>
                    ) : (
                      billingResults.map((soc) => (
                        <Box
                          key={soc.recordId}
                          role="button"
                          onClick={() => attachExistingBilling(soc.recordId)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                            padding: '9px 11px',
                            cursor: 'pointer',
                            borderRadius: dsV2.radius.sm,
                            border: `1px solid ${dsV2.color.border}`,
                            background: dsV2.color.blanc,
                            '&:hover': { borderColor: dsV2.color.bleu, background: dsV2.color.bleuDim },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>{soc.name}</Box>
                            {soc.siret ? (
                              <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>
                                {t('drawers.editClientSociety.siretLabel', {
                                  ns: 'missionDashboard',
                                  siret: soc.siret,
                                })}
                              </Box>
                            ) : null}
                          </Box>
                          <ButtonV2 variant="secondary" size="small" loading={linking} disabled={linking}>
                            {t('drawers.common.attach', { ns: 'missionDashboard' })}
                          </ButtonV2>
                        </Box>
                      ))
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <FieldV2 label={t('drawers.common.name', { ns: 'missionDashboard' })} required>
                    <InputV2
                      value={billingName}
                      onChange={setBillingName}
                      placeholder={t('drawers.common.businessName', { ns: 'missionDashboard' })}
                    />
                  </FieldV2>
                  <Row2>
                    <FieldV2 label={t('drawers.common.legalStatus', { ns: 'missionDashboard' })}>
                      <SelectV2
                        value={billingStatus}
                        onChange={setBillingStatus}
                        options={SOCIETY_STATUS_OPTIONS}
                        placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
                      />
                    </FieldV2>
                    <FieldV2 label={t('drawers.common.siret', { ns: 'missionDashboard' })}>
                      <InputV2 value={billingSiret} onChange={setBillingSiret} />
                    </FieldV2>
                  </Row2>
                  <FieldV2 label={t('drawers.common.tva', { ns: 'missionDashboard' })}>
                    <InputV2
                      value={billingTva}
                      onChange={setBillingTva}
                      placeholder={t('drawers.common.optional', { ns: 'missionDashboard' })}
                    />
                  </FieldV2>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonV2
                      size="small"
                      onClick={createBilling}
                      disabled={!billingName.trim()}
                      loading={savingSociety || linking}
                      startIcon={<SaveRoundedIcon sx={{ fontSize: 14 }} />}
                    >
                      {t('drawers.editClientSociety.createAndAttach', { ns: 'missionDashboard' })}
                    </ButtonV2>
                  </Box>
                </>
              )}
            </>
          )}
        </>
      ) : null}
    </FormDrawer>
  );
};

export default EditClientSocietyDrawer;
